import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PlatformLayout } from "./platform-layout";
import { 
  Loader2,
  Settings,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export function SettingsContent() {
  return (
    <>
      <div className="p-3 sm:p-6 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Platform Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure platform-level integrations and settings
          </p>
        </div>

        <div className="space-y-6">
        {/* AI image generation — platform-wide governance (Task #26) */}
        <AIPlatformImageSettingsCard />

        {/* Platform-default favicon + workspace icon (super-admin) */}
        <PlatformDefaultBrandingCard />

        {/* Task #13 (Clarik rebrand) — platform-default identity:
            primary/secondary/accent + body/display/mono fonts +
            wordmark + powered-by caption. Cleared values fall back to
            the bundled Clarik defaults. */}
        <PlatformDefaultIdentityCard />

        {/* Lead Credits moved to Plans & Pricing hub */}

        {/* Additional Platform Settings can go here */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Platform Information</CardTitle>
            <CardDescription>
              General platform configuration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-muted-foreground">Environment</span>
                <Badge variant={import.meta.env.MODE === 'production' ? 'default' : 'secondary'}>
                  {import.meta.env.MODE === 'production' ? 'Production' : 'Development'}
                </Badge>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground">Platform Version</span>
                <span className="text-sm font-mono">1.0.0</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </>
  );
}

export default function PlatformSettings() {
  return <PlatformLayout><SettingsContent /></PlatformLayout>;
}

// ============================================================================
// Platform-default branding (super-admin)
// ============================================================================
// Hard-coded favicon + workspace icon (sidebar icon) every workspace inherits
// unless its tier allows custom branding AND the workspace admin uploaded an
// override. Stored in `platform_settings` via /api/platform/branding/*.
interface PlatformDefaults {
  faviconPath: string | null;
  faviconFileName: string | null;
  sidebarIconPath: string | null;
  sidebarIconFileName: string | null;
  // Task #13 (Clarik) — full wordmark logo for login/marketing/workspace fallback.
  logoPath: string | null;
  logoFileName: string | null;
}

function PlatformDefaultBrandingCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<PlatformDefaults>({
    queryKey: ["/api/platform/branding/defaults"],
  });
  const [busy, setBusy] = useState<null | "favicon" | "sidebarIcon" | "logo">(null);

  const upload = async (kind: "favicon" | "sidebarIcon" | "logo", file: File) => {
    const isFavicon = kind === "favicon";
    const maxSize = isFavicon ? 1 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({ title: "File too large", description: `Max ${isFavicon ? "1MB" : "5MB"}.`, variant: "destructive" });
      return;
    }
    setBusy(kind);
    try {
      const urlRes = await apiRequest("POST", "/api/platform/branding/upload-url", {
        name: file.name,
        size: file.size,
        contentType: file.type,
      });
      const { uploadURL, objectPath } = await urlRes.json();
      const putRes = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) throw new Error("Upload failed");
      const endpoint =
        kind === "favicon" ? "/api/platform/branding/favicon"
        : kind === "logo" ? "/api/platform/branding/logo"
        : "/api/platform/branding/sidebarIcon";
      await apiRequest("POST", endpoint, { path: objectPath, fileName: file.name });
      toast({
        title:
          kind === "favicon" ? "Platform favicon updated"
          : kind === "logo" ? "Platform logo updated"
          : "Platform workspace icon updated",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/branding/defaults"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding/public"] });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const clearAsset = async (kind: "favicon" | "sidebarIcon" | "logo") => {
    setBusy(kind);
    try {
      const endpoint =
        kind === "favicon" ? "/api/platform/branding/favicon"
        : kind === "logo" ? "/api/platform/branding/logo"
        : "/api/platform/branding/sidebarIcon";
      await apiRequest("DELETE", endpoint);
      toast({ title: "Cleared" });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/branding/defaults"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding/public"] });
    } catch (e: any) {
      toast({ title: "Couldn't clear", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const renderRow = (
    kind: "favicon" | "sidebarIcon" | "logo",
    label: string,
    description: string,
    fileName: string | null,
    previewUrl: string | null,
    accept: string,
  ) => (
    <div className="rounded-lg border p-4 space-y-3" data-testid={`row-platform-${kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
          {fileName && (
            <p className="text-xs text-muted-foreground mt-1" data-testid={`text-platform-${kind}-filename`}>
              Current: <span className="font-mono">{fileName}</span>
            </p>
          )}
        </div>
        {fileName && previewUrl && (
          <img
            src={`${previewUrl}?v=${encodeURIComponent(fileName)}`}
            alt={label}
            className="h-10 w-10 rounded object-contain bg-muted"
            data-testid={`img-platform-${kind}-preview`}
          />
        )}
      </div>
      <div className="flex gap-2">
        <Input
          type="file"
          accept={accept}
          disabled={busy !== null}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload(kind, f);
            e.target.value = "";
          }}
          data-testid={`input-platform-${kind}-file`}
        />
        {fileName && (
          <Button
            type="button"
            variant="outline"
            disabled={busy !== null}
            onClick={() => clearAsset(kind)}
            data-testid={`button-platform-${kind}-clear`}
          >
            Clear
          </Button>
        )}
        {busy === kind && <Loader2 className="h-4 w-4 animate-spin self-center" />}
      </div>
    </div>
  );

  return (
    <Card data-testid="card-platform-default-branding">
      <CardHeader>
        <CardTitle className="text-lg">Platform Default Branding</CardTitle>
        <CardDescription>
          Hard-coded favicon and workspace (sidebar) icon every workspace inherits
          out of the box. Workspaces on tiers that <em>allow custom branding</em> can
          upload their own to override these. Tiers with custom branding turned off
          (set <code>extraConfig.allowsCustomBranding=false</code> on a tier) always
          show these defaults regardless of any custom asset they previously uploaded.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {renderRow(
              "favicon",
              "Default favicon",
              "Browser tab icon. PNG / SVG / ICO recommended, square. Max 1MB.",
              data?.faviconFileName || null,
              "/api/platform/branding/favicon-image",
              "image/png,image/x-icon,image/svg+xml,image/vnd.microsoft.icon",
            )}
            {renderRow(
              "sidebarIcon",
              "Default workspace icon",
              "Shown in the in-app sidebar header for workspaces without their own. PNG / SVG, square. Max 5MB.",
              data?.sidebarIconFileName || null,
              "/api/platform/branding/sidebar-icon-image",
              "image/png,image/jpeg,image/webp,image/svg+xml",
            )}
            {renderRow(
              "logo",
              "Default logo",
              "Full wordmark logo used on the login page, marketing footer, and any workspace that hasn't uploaded its own. PNG / SVG, transparent background recommended. Max 5MB.",
              data?.logoFileName || null,
              null,
              "image/png,image/jpeg,image/webp,image/svg+xml",
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Platform Default Identity (super-admin, Task #13 / Clarik rebrand)
// ============================================================================
// Colors + fonts + wordmark + powered-by caption every workspace inherits
// unless its tier allows custom branding AND the workspace admin overrode
// it via the per-workspace branding dialog. Stored as scalar rows in
// `platform_settings`. Bundled fallbacks (rendered as placeholders) are
// the Clarik defaults shipped in `client/src/index.css`.
interface PlatformIdentity {
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  bodyFontFamily: string | null;
  displayFontFamily: string | null;
  monoFontFamily: string | null;
  wordmarkText: string | null;
  poweredByText: string | null;
}

function PlatformDefaultIdentityCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<PlatformIdentity>({
    queryKey: ["/api/platform/branding/identity"],
  });

  const mut = useMutation({
    mutationFn: async (patch: Partial<PlatformIdentity>) => {
      const res = await apiRequest("PATCH", "/api/platform/branding/identity", patch);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/branding/identity"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding/public"] });
      toast({ title: "Platform identity saved" });
    },
    onError: (e: any) =>
      toast({ title: "Save failed", description: e?.message || "Try again.", variant: "destructive" }),
  });

  const colorField = (key: keyof PlatformIdentity, label: string, fallback: string) => (
    <div className="flex items-center gap-3">
      <Label className="w-32 text-sm">{label}</Label>
      <Input
        type="color"
        defaultValue={(data?.[key] as string) || fallback}
        onBlur={(e) => mut.mutate({ [key]: e.target.value } as Partial<PlatformIdentity>)}
        className="w-14 h-9 p-1 cursor-pointer"
        data-testid={`input-platform-${key}`}
      />
      <Input
        defaultValue={(data?.[key] as string) || ""}
        placeholder={fallback}
        onBlur={(e) =>
          mut.mutate({ [key]: e.target.value.trim() || null } as Partial<PlatformIdentity>)
        }
        className="w-32 font-mono text-sm"
        data-testid={`input-platform-${key}-text`}
      />
    </div>
  );

  const textField = (
    key: keyof PlatformIdentity,
    label: string,
    placeholder: string,
    helper?: string,
  ) => (
    <div className="space-y-1">
      <Label htmlFor={`platform-${key}`} className="text-sm">
        {label}
      </Label>
      <Input
        id={`platform-${key}`}
        defaultValue={(data?.[key] as string) || ""}
        placeholder={placeholder}
        onBlur={(e) =>
          mut.mutate({ [key]: e.target.value.trim() || null } as Partial<PlatformIdentity>)
        }
        data-testid={`input-platform-${key}`}
      />
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  );

  return (
    <Card data-testid="card-platform-default-identity">
      <CardHeader>
        <CardTitle className="text-lg">Default Identity</CardTitle>
        <CardDescription>
          Colors, fonts, wordmark, and "Powered by" caption every workspace inherits.
          Workspaces on tiers that allow custom branding can override any of these from
          their own Branding panel. Empty fields use the bundled Clarik defaults.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="space-y-2">
              <Label className="font-medium">Colors</Label>
              {colorField("primaryColor", "Primary", "#1A4D3A")}
              {colorField("secondaryColor", "Secondary", "#1C1C1A")}
              {colorField("accentColor", "Accent", "#6B6B5E")}
            </div>
            <div className="space-y-2">
              <Label className="font-medium">Typography</Label>
              {textField(
                "bodyFontFamily",
                "Body font",
                '"Inter", system-ui, sans-serif',
              )}
              {textField(
                "displayFontFamily",
                "Display font",
                '"Fraunces", Georgia, serif',
              )}
              {textField(
                "monoFontFamily",
                "Monospace font",
                '"JetBrains Mono", ui-monospace, monospace',
              )}
            </div>
            <div className="space-y-2">
              <Label className="font-medium">Wordmark</Label>
              {textField("wordmarkText", "Wordmark text", "clarik")}
              {textField(
                "poweredByText",
                '"Powered by" caption',
                "Powered by Clarik.io",
                "Used on white-label workspaces that haven't overridden the caption.",
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Eventbrite — platform-wide kill switch (Task #694)
// ============================================================================
// Stored in `platform_settings.eventbrite_enabled`. When OFF, all workspace
// OAuth, events list, settle, and webhook routes return 403 / drop on the floor.
function EventbritePlatformToggleCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/eventbrite/platform-status"],
  });
  const mut = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiRequest("POST", "/api/platform/eventbrite/enabled", { enabled });
      return res.json();
    },
    onSuccess: (_d, vars) => {
      toast({
        title: vars ? "Eventbrite enabled platform-wide" : "Eventbrite disabled platform-wide",
        description: vars
          ? "Workspaces can now connect Eventbrite and receive payouts/buyers."
          : "All Eventbrite OAuth, settlements, and webhooks are now blocked.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/eventbrite/platform-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/integrations/status"] });
    },
    onError: (err: any) => toast({
      title: "Couldn't update",
      description: err?.message || "Try again.",
      variant: "destructive",
    }),
  });

  return (
    <Card data-testid="card-eventbrite-platform-toggle">
      <CardHeader>
        <CardTitle className="text-lg">Eventbrite — Ticketed Bookings</CardTitle>
        <CardDescription>
          Per-workspace OAuth integration. Connected workspaces auto-settle ticketed shows
          using the EB NET payout and import ticket buyers into their marketing list.
          Toggle OFF to instantly cut off all OAuth, settlements, and webhooks platform-wide.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-3">
            <div>
              <p className="text-sm font-medium">Eventbrite enabled</p>
              <p className="text-xs text-muted-foreground">
                {data?.enabled ? "Workspaces can use Eventbrite." : "Disabled — all routes return 403."}
              </p>
            </div>
            <Switch
              checked={!!data?.enabled}
              disabled={mut.isPending}
              onCheckedChange={(v) => mut.mutate(v)}
              data-testid="switch-eventbrite-platform-enabled"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// AI image generation — platform-wide governance (Task #26)
// ============================================================================
// Stored in `platform_settings` as: ai_img_enabled, ai_img_nano_banana_enabled,
// ai_img_runway_enabled, ai_img_per_user_daily_cap. These gate applies on top
// of workspace-level toggles — platform settings are the authoritative gate.

interface AIPlatformImageSettings {
  enabled: boolean;
  nanoBananaEnabled: boolean;
  runwayEnabled: boolean;
  perUserDailyCap: number | null;
}

function AIPlatformImageSettingsCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<AIPlatformImageSettings>({
    queryKey: ["/api/platform/settings/ai-image"],
  });

  const [enabled, setEnabled] = useState(true);
  const [nanoEnabled, setNanoEnabled] = useState(true);
  const [runwayEnabled, setRunwayEnabled] = useState(true);
  const [dailyCap, setDailyCap] = useState<string>("");

  useEffect(() => {
    if (data) {
      setEnabled(!!data.enabled);
      setNanoEnabled(!!data.nanoBananaEnabled);
      setRunwayEnabled(!!data.runwayEnabled);
      setDailyCap(data.perUserDailyCap == null ? "" : String(data.perUserDailyCap));
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", "/api/platform/settings/ai-image", {
        enabled,
        nanoBananaEnabled: nanoEnabled,
        runwayEnabled: runwayEnabled,
        perUserDailyCap: dailyCap.trim() === "" ? null : Math.max(0, Math.floor(Number(dailyCap) || 0)),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/settings/ai-image"] });
      toast({ title: "AI image settings saved" });
    },
    onError: (err: any) =>
      toast({ title: "Failed to save", description: err?.message, variant: "destructive" }),
  });

  return (
    <Card data-testid="card-platform-ai-image-settings">
      <CardHeader>
        <CardTitle className="text-lg">AI Image Generation</CardTitle>
        <CardDescription>
          Platform-wide governance for the "Generate with AI" button in the social media
          library and post composer. These settings override workspace-level toggles.
          Images are billed against each workspace's AI credit pool.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Image generation enabled</div>
                <div className="text-xs text-muted-foreground">
                  Master kill-switch for both providers platform-wide.
                </div>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="switch-platform-ai-image-enabled"
              />
            </div>

            <div className="pl-4 space-y-3">
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="text-sm font-medium">Nano Banana (Gemini)</div>
                  <div className="text-xs text-muted-foreground">
                    Fast, photo-realistic. ~5¢ per image.
                  </div>
                </div>
                <Switch
                  checked={nanoEnabled}
                  onCheckedChange={setNanoEnabled}
                  disabled={!enabled}
                  data-testid="switch-platform-ai-image-nano-banana"
                />
              </div>

              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="text-sm font-medium">Runway Gen-4 Image</div>
                  <div className="text-xs text-muted-foreground">
                    Stylized, cinematic. ~10¢ per image.
                  </div>
                </div>
                <Switch
                  checked={runwayEnabled}
                  onCheckedChange={setRunwayEnabled}
                  disabled={!enabled}
                  data-testid="switch-platform-ai-image-runway"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="platform-ai-image-cap">Per-user daily cap (images)</Label>
              <Input
                id="platform-ai-image-cap"
                type="number"
                min={0}
                step={1}
                value={dailyCap}
                onChange={(e) => setDailyCap(e.target.value)}
                placeholder="No cap"
                disabled={!enabled}
                data-testid="input-platform-ai-image-daily-cap"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Caps how many images each user can generate per UTC day. Leave empty for no
                cap. The workspace overage cap above still applies on top.
              </p>
            </div>

            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              data-testid="button-save-platform-ai-image-settings"
            >
              {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

